import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar, DollarSign, BedDouble, MapPin, Sparkles, User,
  ArrowRight, ArrowLeft, Check, Loader2,
} from "lucide-react";
import { API } from "../App";
import { useSEO } from "../lib/useSEO";
import { getAttribution } from "../lib/attribution";

/**
 * /get-started — Apartment Match Wizard.
 *
 * Mobile-first six-step funnel. Single source of truth for Bryan's marketing
 * channels (Instagram, TikTok, QR codes, email signature).
 *
 * The component is intentionally vanilla — no external form library — so it
 * stays under 50 lines of state and renders instantly on slow mobile networks.
 */

const TIMELINES = [
  { id: "asap",       label: "ASAP",            sub: "Within the next week" },
  { id: "30d",        label: "Within 30 days",  sub: "Move-in this month" },
  { id: "60d",        label: "1–2 months",      sub: "Plenty of time to plan" },
  { id: "exploring",  label: "Just exploring",  sub: "Not sure yet" },
];

const BUDGETS = [
  { id: "studio_under_1k",  label: "Studio under $1,000",  min: 0,    max: 1000 },
  { id: "1000_1500",        label: "$1,000 – $1,500",      min: 1000, max: 1500 },
  { id: "1500_2000",        label: "$1,500 – $2,000",      min: 1500, max: 2000 },
  { id: "2000_2500",        label: "$2,000 – $2,500",      min: 2000, max: 2500 },
  { id: "2500_plus",        label: "$2,500+",              min: 2500, max: 6000 },
];

const BEDROOMS = ["Studio", "1BD", "2BD", "3BD+"];

const NEIGHBORHOODS = [
  "Energy Corridor", "The Heights", "Midtown", "Montrose",
  "Downtown", "Medical Center", "Sugar Land", "Katy",
  "Open to suggestions",
];

const MUST_HAVES = [
  "Pet-friendly", "Parking", "Pool", "Gym",
  "Work-from-home space", "In-unit washer/dryer",
  "Short commute", "No preference",
];

const HEARD_OPTIONS = [
  "Google", "Instagram", "TikTok", "YouTube", "Referral", "Other",
];

const TOTAL_STEPS = 6;

const ProgressBar = ({ step }) => (
  <div className="w-full bg-slate-100" data-testid="wizard-progress">
    <div
      className="h-1.5 bg-[#1F2A44] transition-all duration-500"
      style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
    />
  </div>
);

const StepShell = ({ icon: Icon, eyebrow, title, sub, children, onBack, onNext, canAdvance,
                    nextLabel = "Continue", submitting = false, isLastStep = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -16 }}
    transition={{ duration: 0.28 }}
    className="w-full max-w-xl mx-auto p-6 sm:p-8"
  >
    <div className="flex items-center gap-2 text-[#DE6E4B] text-xs uppercase tracking-widest font-bold">
      <Icon className="w-4 h-4" /> {eyebrow}
    </div>
    <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display'] leading-tight">
      {title}
    </h1>
    {sub && <p className="mt-2 text-slate-600 text-sm sm:text-base">{sub}</p>}

    <div className="mt-6 space-y-3">{children}</div>

    <div className="mt-8 flex items-center justify-between gap-3">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-slate-600 hover:text-[#1F2A44] transition"
          data-testid="wizard-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      ) : <span />}
      <button
        type="button"
        onClick={onNext}
        disabled={!canAdvance || submitting}
        data-testid={isLastStep ? "wizard-submit" : "wizard-next"}
        className="inline-flex items-center gap-2 px-6 py-3 bg-[#DE6E4B] text-white font-bold rounded-xl hover:bg-[#c75f3f] disabled:opacity-40 disabled:cursor-not-allowed transition shadow-sm"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {isLastStep ? (submitting ? "Sending…" : "See My Matches") : nextLabel}
        {!submitting && <ArrowRight className="w-4 h-4" />}
      </button>
    </div>
  </motion.div>
);

const OptionCard = ({ label, sub, selected, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full text-left px-5 py-4 rounded-xl border-2 transition active:scale-[0.99] ${
      selected
        ? "border-[#DE6E4B] bg-[#FFF6F1]"
        : "border-slate-200 hover:border-[#1F2A44] bg-white"
    }`}
  >
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="font-semibold text-[#1F2A44]">{label}</p>
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      </div>
      {selected && (
        <div className="w-6 h-6 rounded-full bg-[#DE6E4B] text-white flex items-center justify-center">
          <Check className="w-4 h-4" />
        </div>
      )}
    </div>
  </button>
);

const Chip = ({ label, selected, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition active:scale-95 ${
      selected
        ? "border-[#DE6E4B] bg-[#DE6E4B] text-white"
        : "border-slate-300 text-slate-700 hover:border-[#1F2A44] bg-white"
    }`}
  >
    {label}
  </button>
);

const toggleInList = (list, value) =>
  list.includes(value) ? list.filter((x) => x !== value) : [...list, value];

const GetStartedWizard = () => {
  const navigate = useNavigate();
  useSEO({
    title: "Get Started · Find Your Houston Apartment | Urban Nest Quest",
    description: "Take the 60-second quiz and Bryan will text your top 3 Houston apartment matches within the hour. Free service, no obligation.",
    path: "/get-started",
  });

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    moveDate: "",
    timeline: "",
    budgetId: "",
    bedrooms: "",
    neighborhoods: [],
    mustHaves: [],
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    heardAbout: "",
  });

  const budget = useMemo(
    () => BUDGETS.find((b) => b.id === form.budgetId),
    [form.budgetId],
  );

  const canAdvance = useMemo(() => {
    switch (step) {
      case 1: return !!(form.timeline || form.moveDate);
      case 2: return !!form.budgetId;
      case 3: return !!form.bedrooms;
      case 4: return form.neighborhoods.length > 0;
      case 5: return form.mustHaves.length > 0;
      case 6:
        return form.firstName.trim() && form.phone.trim() && /\S+@\S+\.\S+/.test(form.email);
      default: return false;
    }
  }, [step, form]);

  const next = () => {
    if (!canAdvance) return;
    if (step < TOTAL_STEPS) setStep(step + 1);
    else handleSubmit();
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const attribution = getAttribution();
      const payload = {
        name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim() || form.firstName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        lead_type: "renter",
        source: "get-started",
        budget_min: budget?.min ?? null,
        budget_max: budget?.max ?? null,
        preferred_areas: form.neighborhoods,
        move_in_date: form.moveDate || null,
        move_in_timeline: form.timeline || null,
        bedrooms: form.bedrooms || null,
        lifestyle_priorities: form.mustHaves,
        must_haves: form.mustHaves,
        heard_about: form.heardAbout || null,
        message: `Wizard submission. Heard about us via: ${form.heardAbout || "n/a"}.`,
        ...attribution,
      };
      await axios.post(`${API}/leads`, payload);
      navigate("/get-started/thank-you", { state: { firstName: form.firstName.trim() } });
    } catch (err) {
      console.error("Wizard submit failed", err);
      alert("Something went wrong. Please call (936) 224-1484 or try again in a moment.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-[#FAF7F2] pt-16" data-testid="get-started-wizard">
      <ProgressBar step={step} />
      <div className="text-center text-xs text-slate-400 pt-3" data-testid="wizard-step-label">
        Step {step} of {TOTAL_STEPS}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <StepShell
            key="s1"
            icon={Calendar}
            eyebrow="Step 1 of 6"
            title="When do you need to move?"
            sub="Pick a date or a timeline — we'll match the urgency."
            onNext={next}
            canAdvance={canAdvance}
          >
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Move-in date</span>
              <input
                type="date"
                value={form.moveDate}
                onChange={(e) => setForm({ ...form, moveDate: e.target.value })}
                className="mt-1 w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base focus:border-[#DE6E4B] outline-none"
                data-testid="wizard-move-date"
              />
            </label>
            <div className="text-center text-xs text-slate-400 my-1">— or —</div>
            <div className="grid gap-2">
              {TIMELINES.map((t) => (
                <OptionCard
                  key={t.id}
                  label={t.label}
                  sub={t.sub}
                  selected={form.timeline === t.id}
                  onClick={() => setForm({ ...form, timeline: t.id })}
                  testId={`timeline-${t.id}`}
                />
              ))}
            </div>
          </StepShell>
        )}

        {step === 2 && (
          <StepShell
            key="s2"
            icon={DollarSign}
            eyebrow="Step 2 of 6"
            title="What's your monthly budget?"
            sub="We'll show options ±$200 around your range."
            onBack={back}
            onNext={next}
            canAdvance={canAdvance}
          >
            <div className="grid gap-2">
              {BUDGETS.map((b) => (
                <OptionCard
                  key={b.id}
                  label={b.label}
                  selected={form.budgetId === b.id}
                  onClick={() => setForm({ ...form, budgetId: b.id })}
                  testId={`budget-${b.id}`}
                />
              ))}
            </div>
          </StepShell>
        )}

        {step === 3 && (
          <StepShell
            key="s3"
            icon={BedDouble}
            eyebrow="Step 3 of 6"
            title="How many bedrooms?"
            onBack={back}
            onNext={next}
            canAdvance={canAdvance}
          >
            <div className="grid grid-cols-2 gap-2">
              {BEDROOMS.map((b) => (
                <OptionCard
                  key={b}
                  label={b}
                  selected={form.bedrooms === b}
                  onClick={() => setForm({ ...form, bedrooms: b })}
                  testId={`bedrooms-${b.toLowerCase().replace(/\W/g, "-")}`}
                />
              ))}
            </div>
          </StepShell>
        )}

        {step === 4 && (
          <StepShell
            key="s4"
            icon={MapPin}
            eyebrow="Step 4 of 6"
            title="Which Houston neighborhoods?"
            sub="Pick one or more — or tell us you're open to ideas."
            onBack={back}
            onNext={next}
            canAdvance={canAdvance}
          >
            <div className="flex flex-wrap gap-2">
              {NEIGHBORHOODS.map((n) => (
                <Chip
                  key={n}
                  label={n}
                  selected={form.neighborhoods.includes(n)}
                  onClick={() => setForm({ ...form, neighborhoods: toggleInList(form.neighborhoods, n) })}
                  testId={`neighborhood-${n.toLowerCase().replace(/\s+/g, "-")}`}
                />
              ))}
            </div>
          </StepShell>
        )}

        {step === 5 && (
          <StepShell
            key="s5"
            icon={Sparkles}
            eyebrow="Step 5 of 6"
            title="Any must-haves?"
            sub="Tap everything that matters. We'll filter for them."
            onBack={back}
            onNext={next}
            canAdvance={canAdvance}
          >
            <div className="flex flex-wrap gap-2">
              {MUST_HAVES.map((m) => (
                <Chip
                  key={m}
                  label={m}
                  selected={form.mustHaves.includes(m)}
                  onClick={() => setForm({ ...form, mustHaves: toggleInList(form.mustHaves, m) })}
                  testId={`musthave-${m.toLowerCase().replace(/\W/g, "-")}`}
                />
              ))}
            </div>
          </StepShell>
        )}

        {step === 6 && (
          <StepShell
            key="s6"
            icon={User}
            eyebrow="Last step"
            title="How do we reach you?"
            sub="Bryan will text your top 3 picks within the hour."
            onBack={back}
            onNext={next}
            canAdvance={canAdvance}
            isLastStep
            submitting={submitting}
          >
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text" placeholder="First name *" required
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base focus:border-[#DE6E4B] outline-none"
                data-testid="wizard-first-name"
              />
              <input
                type="text" placeholder="Last name"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base focus:border-[#DE6E4B] outline-none"
                data-testid="wizard-last-name"
              />
            </div>
            <input
              type="tel" placeholder="Phone *" required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base focus:border-[#DE6E4B] outline-none"
              data-testid="wizard-phone"
            />
            <input
              type="email" placeholder="Email *" required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base focus:border-[#DE6E4B] outline-none"
              data-testid="wizard-email"
            />
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">How did you hear about us?</span>
              <select
                value={form.heardAbout}
                onChange={(e) => setForm({ ...form, heardAbout: e.target.value })}
                className="mt-1 w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base focus:border-[#DE6E4B] outline-none bg-white"
                data-testid="wizard-heard-about"
              >
                <option value="">Select one (optional)</option>
                {HEARD_OPTIONS.map((h) => (
                  <option key={h} value={h.toLowerCase()}>{h}</option>
                ))}
              </select>
            </label>
            <p className="mt-3 text-xs text-slate-400 leading-relaxed">
              By submitting, you agree to receive a text from Urban Nest Quest about your apartment search. Reply STOP to opt out anytime.
            </p>
          </StepShell>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GetStartedWizard;
